import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  BadgeCheck,
  Award,
  Building2,
  Leaf,
  Clock,
  Users,
  Bus,
  MapPin,
  Headphones,
  CalendarDays,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import {
  company,
  heroSlides,
  stats,
  services,
  whyUs,
  news,
  aboutIntro,
  credentials,
} from "../mock";
import { Reveal, CountUp, SectionLabel } from "../components/Shared";
import useSEO from "../hooks/useSEO";

const iconMap = {
  ShieldCheck,
  BadgeCheck,
  Award,
  Building2,
  Leaf,
  Clock,
  Users,
  Bus,
  MapPin,
  Headphones,
};

const Home = () => {
  useSEO({
    title: "Kocaeli Personel ve Öğrenci Taşımacılığı",
    description:
      "Nikomedya Turizm - 1983'ten beri Kocaeli'de personel taşımacılığı, öğrenci taşımacılığı, VIP taşımacılık ve araç kiralama. 42 yılı aşkın tecrübe, belgeli kalite.",
  });

  const [slide, setSlide] = useState(0);
  const [activeService, setActiveService] = useState(0);

  const nextSlide = useCallback(
    () => setSlide((s) => (s + 1) % heroSlides.length),
    []
  );
  const prevSlide = () =>
    setSlide((s) => (s - 1 + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    const t = setInterval(nextSlide, 7500);
    return () => clearInterval(t);
  }, [nextSlide]);

  const active = heroSlides[slide];

  return (
    <div>
      {/* ================= HERO ================= */}
      <section className="relative min-h-[88vh] flex flex-col overflow-hidden bg-[#050e1d]">
        {heroSlides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ${
              i === slide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={s.image}
              alt={s.title}
              className={`w-full h-full object-cover ${
                i === slide ? "hero-kenburns" : ""
              }`}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050e1d]/95 via-[#081f3b]/70 to-[#0d2b4f]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050e1d]/90 via-transparent to-[#050e1d]/40" />

        {/* İnce dikey çizgiler */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          <div className="max-w-7xl mx-auto h-full px-8 flex justify-between">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="w-px h-full bg-white/[0.05]" />
            ))}
          </div>
        </div>

        <div className="relative flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center pt-36 pb-14 lg:pt-32">
          <div key={active.id} className="hero-fade-enter max-w-3xl">
            <div className="flex items-center gap-4 text-white/70 uppercase tracking-[0.3em] text-[11px] sm:text-xs font-bold">
              <span className="h-px w-14 fine-line" />
              {active.badge}
            </div>
            <h1 className="mt-6 text-3xl sm:text-4xl lg:text-[46px] font-bold text-white leading-[1.15] tracking-tight">
              {active.title}
            </h1>
            <p className="mt-5 text-white/65 text-[15px] sm:text-base leading-relaxed max-w-lg">
              {active.description}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={company.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
              >
                <MessageCircle size={17} />
                WhatsApp'tan Teklif Al
              </a>
              <Link
                to={active.cta.path}
                className="btn-outline-light inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
              >
                {active.cta.label}
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          {/* Slider kontrol + sayacı */}
          <div className="absolute right-4 sm:right-6 lg:right-8 bottom-40 hidden md:flex flex-col items-center gap-5">
            <span className="text-white/60 text-sm font-bold tracking-widest [writing-mode:vertical-rl]">
              0{slide + 1} — 0{heroSlides.length}
            </span>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={prevSlide}
                aria-label="Önceki"
                className="w-11 h-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#0d2b4f] transition-colors duration-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={nextSlide}
                aria-label="Sonraki"
                className="w-11 h-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#0d2b4f] transition-colors duration-300"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats band */}
        <div className="relative z-10 border-t border-white/10 bg-[#050e1d]/50 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
              {stats.map((st) => (
                <div key={st.label} className="py-7 px-4 sm:px-8">
                  <div className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                    <CountUp end={st.value} suffix={st.suffix} prefix={st.prefix || ""} />
                  </div>
                  <div className="mt-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/45 font-bold">
                    {st.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-[130px] hidden lg:block">
          <ChevronDown size={22} className="scroll-indicator text-white/50" />
        </div>
      </section>

      {/* ================= CREDENTIALS BAND ================= */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-slate-100">
            {credentials.map((c, i) => {
              const Icon = iconMap[c.icon];
              return (
                <Reveal key={c.title} delay={i * 80}>
                  <div className="credential-item flex items-start gap-4 px-6 py-8 h-full">
                    <Icon size={26} className="text-[#1e9cd7] shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-[15px] font-bold text-[#0d2b4f]">
                        {c.title}
                      </h3>
                      <p className="mt-1 text-[13px] text-[#5b6b7d] leading-relaxed">
                        {c.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= SERVICES SHOWCASE ================= */}
      <section className="py-28 lg:py-32 bg-white relative overflow-hidden">
        <span
          className="absolute -top-6 right-0 text-outline font-extrabold leading-none select-none pointer-events-none hidden xl:block"
          style={{ fontSize: "200px", fontFamily: "Manrope, sans-serif" }}
          aria-hidden="true"
        >
          01
        </span>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel num="01">Hizmetlerimiz</SectionLabel>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-[#0d2b4f] leading-snug tracking-tight max-w-xl">
                Her Yolculukta Güven Yanınızda
              </h2>
              <p className="text-[#5b6b7d] max-w-sm text-[15px] leading-relaxed lg:pb-2">
                Personel ve öğrenci taşımacılığında, deneyim ve kalite
                belgeleriyle desteklenen profesyonel hizmet sunuyoruz.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            {/* Service rows */}
            <Reveal direction="left">
              <div>
                {services.map((s, i) => (
                  <Link
                    key={s.id}
                    to={`/hizmetlerimiz#${s.id}`}
                    onMouseEnter={() => setActiveService(i)}
                    className={`service-row group flex items-center justify-between gap-6 py-7 border-b border-slate-200 ${
                      i === 0 ? "border-t" : ""
                    }`}
                  >
                    <div className="flex items-start gap-5 sm:gap-7">
                      <span
                        className={`text-sm font-extrabold pt-2 transition-colors duration-300 ${
                          activeService === i
                            ? "text-[#1e9cd7]"
                            : "text-slate-300"
                        }`}
                      >
                        0{i + 1}
                      </span>
                      <div>
                        <h3
                          className={`text-lg sm:text-xl font-semibold tracking-tight transition-colors duration-300 ${
                            activeService === i
                              ? "text-[#1e9cd7]"
                              : "text-[#0d2b4f]"
                          }`}
                        >
                          {s.title}
                        </h3>
                        <p
                          className={`mt-2 text-sm text-[#5b6b7d] leading-relaxed max-w-md transition-opacity duration-300 ${
                            activeService === i ? "opacity-100" : "opacity-60"
                          }`}
                        >
                          {s.short}
                        </p>
                      </div>
                    </div>
                    <span className="service-arrow w-12 h-12 rounded-full border border-slate-200 text-[#0d2b4f] flex items-center justify-center shrink-0">
                      <ArrowUpRight size={19} />
                    </span>
                  </Link>
                ))}
              </div>
            </Reveal>

            {/* Sticky image panel */}
            <Reveal direction="right" className="hidden lg:block">
              <div className="sticky top-32 relative h-[540px] rounded-2xl overflow-hidden shadow-2xl">
                {services.map((s, i) => (
                  <img
                    key={s.id}
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                      activeService === i
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-105"
                    }`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050e1d]/85 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-9">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#1e9cd7] font-bold">
                    Nikomedya Turizm
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-white tracking-tight">
                    {services[activeService].title}
                  </h3>
                  <Link
                    to={`/hizmetlerimiz#${services[activeService].id}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-white/90 hover:text-[#1e9cd7] transition-colors"
                  >
                    Detaylı Bilgi
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section className="py-24 lg:py-28 bg-[#f5f8fb] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <Reveal direction="left">
              <div className="relative">
                <img
                  src="/images/trans-1.webp"
                  alt="Nikomedya Turizm araç filosu"
                  loading="lazy"
                  className="rounded-2xl w-[85%] shadow-2xl"
                />
                <img
                  src="/images/trans-2.webp"
                  alt="Nikomedya Turizm taşımacılık"
                  loading="lazy"
                  className="absolute -bottom-12 right-0 w-[55%] rounded-2xl border-[10px] border-[#f5f8fb] shadow-2xl"
                />
                <div className="absolute -bottom-8 left-6 sm:left-10 bg-[#0d2b4f] text-white rounded-2xl px-7 py-5 shadow-2xl">
                  <div className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1e9cd7]">
                    <CountUp end={42} suffix="+" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-80 mt-1">
                    Yılı Aşkın Tecrübe
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal direction="right">
              <SectionLabel num="02">Kurumsal</SectionLabel>
              <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-[#0d2b4f] leading-snug tracking-tight">
                {aboutIntro.title}
              </h2>
              <p className="mt-6 text-[#5b6b7d] leading-relaxed text-[15px]">
                {aboutIntro.paragraphs[0]}
              </p>
              <p className="mt-4 text-[#5b6b7d] leading-relaxed text-[15px]">
                {aboutIntro.paragraphs[2]}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link
                  to="/kurumsal/hakkimizda"
                  className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-sm"
                >
                  Hakkımızda
                  <ArrowUpRight size={17} />
                </Link>
                <a
                  href={company.phoneHref}
                  className="group flex items-center gap-4"
                >
                  <span className="w-14 h-14 rounded-full bg-white text-[#1e9cd7] shadow-md flex items-center justify-center group-hover:bg-[#1e9cd7] group-hover:text-white transition-colors duration-300">
                    <Phone size={20} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#5b6b7d]">
                      Bizi Arayın
                    </span>
                    <span className="block text-base font-extrabold text-[#0d2b4f] tracking-tight">
                      {company.phone}
                    </span>
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= WHY US (dark corporate grid) ================= */}
      <section className="py-24 lg:py-28 bg-[#050e1d] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "url('/images/hero-2.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel num="03" light>
              Neden Nikomedya?
            </SectionLabel>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-white leading-snug tracking-tight max-w-xl">
                Tecrübe ve Güvenin Buluştuğu Nokta
              </h2>
              <p className="text-white/50 max-w-sm text-[15px] leading-relaxed lg:pb-2">
                42 yılı aşkın kurumsal tecrübemizi, belgeli kalite
                standartlarıyla birleştiriyoruz.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {whyUs.map((w) => {
                const Icon = iconMap[w.icon];
                return (
                  <div key={w.title} className="grid-cell bg-[#050e1d] p-9 lg:p-11">
                    <div className="cell-icon w-14 h-14 rounded-full bg-[#1e9cd7]/10 text-[#1e9cd7] flex items-center justify-center">
                      <Icon size={25} />
                    </div>
                    <h3 className="mt-6 text-lg font-semibold text-white tracking-tight">
                      {w.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/50">
                      {w.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= NEWS ================= */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel num="04">Blog & Haberler</SectionLabel>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-[#0d2b4f] leading-snug tracking-tight max-w-xl">
                Güncel Haberler, Sektörel Bilgiler
              </h2>
              <Link
                to="/haberler"
                className="group inline-flex items-center gap-2 text-[#0d2b4f] font-bold text-sm uppercase tracking-[0.15em] hover:text-[#1e9cd7] transition-colors lg:pb-2"
              >
                Tüm Haberler
                <ArrowUpRight
                  size={17}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {news.map((n, i) => (
              <Reveal key={n.id} delay={i * 110}>
                <Link
                  to={`/haberler/${n.id}`}
                  className="news-card group block bg-white rounded-2xl overflow-hidden border border-slate-200"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={n.image}
                      alt={n.title}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 bg-[#050e1d]/80 backdrop-blur-sm text-white text-[11px] font-bold px-3.5 py-1.5 rounded-full flex items-center gap-1.5">
                      <CalendarDays size={12} className="text-[#1e9cd7]" />
                      {n.date}
                    </span>
                  </div>
                  <div className="p-7">
                    <span className="flex items-center gap-1.5 text-xs text-[#5b6b7d]">
                      <Eye size={13} className="text-[#1e9cd7]" />
                      {n.views} Görüntülenme
                    </span>
                    <h3 className="mt-3 text-[16px] font-semibold text-[#0d2b4f] leading-snug tracking-tight group-hover:text-[#1e9cd7] transition-colors line-clamp-2">
                      {n.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#5b6b7d] leading-relaxed line-clamp-2">
                      {n.excerpt}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.12em] text-[#1e9cd7]">
                      Devamını Oku
                      <ArrowRight
                        size={14}
                        className="transition-transform duration-300 group-hover:translate-x-1.5"
                      />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
